import React, { useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';

import BadgeCard from './BadgeCard';
import { gridItemWidth, useRelicVaultLayout } from '../shared/useRelicVaultLayout';
import { COLORS, bodyFont, em, headingFont, panelSurface } from '../shared/theme';
import { webOnly } from 'utils/relicVault/webStyle';

import { TYPE } from 'style/typography';

/**
 * The Badges tab: every badge in the catalog, grouped into the backend's
 * categories, each section showing how many of it you've collected.
 *
 * Badges are earned by playing, never bought, so this is a collection view -
 * tapping one opens its detail popup with how to earn it.
 */
const BadgesView = ({ catalog, ownedBadgeIds, onSelect }) => {
  const { isTablet, isPhone } = useRelicVaultLayout();
  const owned = useMemo(() => new Set(ownedBadgeIds), [ownedBadgeIds]);

  const columns = isPhone ? 3 : isTablet ? 4 : 6;
  const gap = isPhone ? 10 : 14;
  const total = catalog.badges.length;

  return (
    <View>
      <View style={styles.summary}>
        <Text style={styles.summaryLabel}>Badges collected</Text>
        <Text style={styles.summaryValue}>
          {catalog.badges.filter(b => owned.has(b.badgeId)).length} / {total}
        </Text>
      </View>

      {catalog.badgeCategories.map(category => (
        <BadgeSection
          key={category.categoryKey}
          category={category}
          catalog={catalog}
          owned={owned}
          columns={columns}
          gap={gap}
          onSelect={onSelect}
        />
      ))}
    </View>
  );
};

const BadgeSection = ({ category, catalog, owned, columns, gap, onSelect }) => {
  const [width, setWidth] = useState(0);
  const badges = category.badges || [];
  const ownedCount = badges.filter(b => owned.has(b.badgeId)).length;
  const size = gridItemWidth(width, columns, gap);

  return (
    <View style={styles.section}>
      {/* Section banner: title, blurb and the collected count. */}
      <View style={styles.banner}>
        <View style={styles.bannerText}>
          <Text style={styles.bannerTitle}>{category.label}</Text>
          {!!category.blurb && <Text style={styles.bannerBlurb}>{category.blurb}</Text>}
        </View>
        <Text style={styles.bannerCount}>
          Collected: {ownedCount}/{badges.length}
        </Text>
      </View>

      <View
        style={[styles.grid, { marginBottom: -gap }]}
        onLayout={e => setWidth(e.nativeEvent.layout.width)}>
        {width > 0 &&
          badges.map((badge, index) => (
            <View
              key={badge.badgeId}
              style={{
                marginRight: (index + 1) % columns === 0 ? 0 : gap,
                marginBottom: gap,
              }}>
              <BadgeCard
                badge={badge}
                owned={owned.has(badge.badgeId)}
                rarityColor={catalog.tierByKey.get(badge.rarity)?.colorHex || '#4ADE80'}
                size={size}
                onSelect={onSelect}
              />
            </View>
          ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  summary: {
    ...panelSurface,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1.5,
    borderColor: 'rgba(139,111,255,0.30)',
    borderRadius: 18,
    paddingVertical: 14,
    paddingHorizontal: 20,
  },
  summaryLabel: {
    ...bodyFont('800'),
    fontSize: TYPE.xs,
    letterSpacing: em(0.12, 11),
    textTransform: 'uppercase',
    color: COLORS.muted,
  },
  summaryValue: {
    ...headingFont('800'),
    fontSize: TYPE.base,
    color: COLORS.gold,
  },
  section: {
    marginTop: 26,
  },
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    backgroundColor: 'rgba(139,92,246,0.14)',
    borderWidth: 1.5,
    borderColor: 'rgba(139,111,255,0.35)',
    borderRadius: 14,
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginBottom: 16,
    ...webOnly({
      backgroundImage:
        'linear-gradient(90deg, rgba(139,92,246,0.28), rgba(139,92,246,0.06))',
    }),
  },
  bannerText: {
    flexShrink: 1,
    minWidth: 0,
  },
  bannerTitle: {
    ...headingFont('800'),
    fontSize: TYPE.base,
    letterSpacing: em(0.04, 16),
    textTransform: 'uppercase',
    color: COLORS.text,
  },
  bannerBlurb: {
    ...bodyFont('600'),
    fontSize: TYPE.xs,
    color: COLORS.lilac,
    marginTop: 3,
  },
  bannerCount: {
    ...headingFont('800'),
    fontSize: TYPE.xs,
    color: COLORS.gold,
    marginLeft: 12,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  },
});

export default BadgesView;
